import type { ReactNode } from "react";
import type { DomainPage } from "@/types";
import { formatRelativeTime } from "@/utils/formatters";

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

type Props = {
  page: DomainPage;
  onWatchPage?: (page: DomainPage) => void;
};

export default function PageStatusLabel({ page, onWatchPage }: Props) {
  const watchable = Boolean(page.watchable && onWatchPage);

  if (page.evaluation_status === "failed") {
    const reason = page.evaluation_failure_reason?.trim() || "Evaluation failed.";

    return (
      <span className="group relative inline-flex max-w-full items-center">
        {watchable ? (
          <button
            type="button"
            className="cursor-pointer text-sm font-medium text-error hover:underline hover:underline-offset-[3px]"
            aria-label="Watch evaluation in player"
            aria-describedby={`page-eval-failed-${page.id}`}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onWatchPage?.(page)}
          >
            Evaluation failed
          </button>
        ) : (
          <button
            type="button"
            className="cursor-pointer text-sm font-medium text-error"
            aria-describedby={`page-eval-failed-${page.id}`}
          >
            Evaluation failed
          </button>
        )}
        <span
          id={`page-eval-failed-${page.id}`}
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 hidden w-64 -translate-x-1/2 rounded bg-gray-900 px-3 py-2 text-xs leading-relaxed text-white shadow-lg group-hover:block group-focus-within:block"
        >
          {reason}
        </span>
      </span>
    );
  }

  if (page.evaluation_status === "evaluating") {
    const startedLabel = page.evaluation_started_at ? formatRelativeTime(page.evaluation_started_at) : null;
    const text = (
      <>
        Evaluating
        {startedLabel ? ` · ${startedLabel}` : null}
      </>
    );

    return (
      <WatchableStatus watchable={watchable} onWatch={() => onWatchPage?.(page)} className="text-primary animate-pulse">
        {text}
      </WatchableStatus>
    );
  }

  if (page.evaluation_status === "completed") {
    const issues = `${page.issue_count} ${page.issue_count === 1 ? "issue" : "issues"}`;
    const finishedAt = page.evaluation_finished_at;

    if (finishedAt && Date.now() - new Date(finishedAt).getTime() >= TWENTY_FOUR_HOURS_MS) {
      return (
        <WatchableStatus watchable={watchable} onWatch={() => onWatchPage?.(page)} className="text-gray-700">
          Last evaluated {formatRelativeTime(finishedAt)} · {issues}
        </WatchableStatus>
      );
    }

    return (
      <WatchableStatus watchable={watchable} onWatch={() => onWatchPage?.(page)} className="text-gray-700">
        Evaluation complete · {issues}
      </WatchableStatus>
    );
  }

  return <span className="text-sm font-medium text-gray-500">Not evaluated</span>;
}

function WatchableStatus({
  watchable,
  onWatch,
  className,
  children,
}: {
  watchable: boolean;
  onWatch: () => void;
  className: string;
  children: ReactNode;
}) {
  if (!watchable) {
    return <span className={`text-sm font-medium ${className}`}>{children}</span>;
  }

  return (
    <button
      type="button"
      className={`cursor-pointer text-sm font-medium hover:underline hover:underline-offset-[3px] ${className}`}
      aria-label="Watch evaluation in player"
      onMouseDown={(event) => event.preventDefault()}
      onClick={onWatch}
    >
      {children}
    </button>
  );
}
